import { Tooltip } from '@axellero/shared';
import { Stack } from '@mui/material';
import { forwardRef, useMemo } from 'react';
import { Position, useEdges } from 'react-flow-renderer';

import { mapNodeParamToString } from '../../services/mapNodeParamToString';
import { mapNodeParamTypeToColor } from '../../services/mapNodeParamTypeToColor';
import { mapSourceTypeToIcon } from '../../services/mapSourceTypeToIcon';
import { NodeParamTypes } from '../../types/NodeParamTypes';
import { FlowButtonHandle } from '../FlowButtonHandle';
import type { ParamButtonHandleProps } from '../ParamButtonHandle';
import { ParamButtonHandle } from '../ParamButtonHandle';
import type { InputHandleStackProps } from './props';

export const InputHandleStack = forwardRef<HTMLDivElement, InputHandleStackProps>(
  ({ id, sx, parameters, disableFlow, ...rest }, ref) => {
    const edges = useEdges();

    const relatedEdges = useMemo(() => edges.filter((edge) => edge.target === id), [edges, id]);

    const isFlowActive = useMemo(
      () => relatedEdges.some((edge) => edge.type === 'flow'),
      [relatedEdges]
    );

    return (
      <Stack ref={ref} {...rest} sx={{ width: '100%', ...sx }}>
        {!disableFlow && (
          <FlowButtonHandle active={isFlowActive} type="target" position={Position.Left} />
        )}
        {parameters.map((param) => (
          <Tooltip
            key={param.id}
            arrow
            enterDelay={1000}
            placement="left"
            title={mapNodeParamToString(param)}
          >
            <ParamButtonHandle
              id={param.id}
              name={param.name}
              array={param.isArray}
              object={param.type === NodeParamTypes.Object}
              type="target"
              position={Position.Left}
              required={param.isRequired}
              icon={mapSourceTypeToIcon(param.source.sourceType)}
              color={mapNodeParamTypeToColor(param.type) as ParamButtonHandleProps['color']}
            />
          </Tooltip>
        ))}
      </Stack>
    );
  }
);

InputHandleStack.displayName = 'InputHandleStack';
